import { useCallback, useState } from "react";

/**
 * Manages a boolean expanded/collapsed state, backed by localStorage when a
 * `storageKey` is given (omit it for state that should not persist).
 * Returns [isExpanded, toggle].
 */
export function useCollapsedState(
  storageKey: string | undefined,
  defaultExpanded = true,
): [boolean, () => void] {
  const [isExpanded, setIsExpanded] = useState<boolean>(() => {
    if (!storageKey) return defaultExpanded;
    try {
      const val = localStorage.getItem(storageKey);
      if (val === "true") return true;
      if (val === "false") return false;
      return defaultExpanded;
    } catch {
      return defaultExpanded;
    }
  });

  const toggle = useCallback(() => {
    setIsExpanded((prev) => {
      const next = !prev;
      if (storageKey) {
        try {
          localStorage.setItem(storageKey, String(next));
        } catch {
          // Ignore write failures (quota exceeded, privacy mode)
        }
      }
      return next;
    });
  }, [storageKey]);

  return [isExpanded, toggle];
}
